import { useApiMutation, useApiQuery } from './useApi';
import { useQueryClient } from 'react-query';
import { invalidateCrmQueries } from './useCrm';

export type CrmCalendarEventStatus = 'pending' | 'attached' | 'auto_ignored' | 'dismissed';

/** Une réunion d'agenda Google d'un admin, telle que la file « à associer » la présente. */
export type CrmCalendarEvent = {
    id: number;
    title: string | null;
    owner_email: string;
    starts_at: string;
    duration_minutes: number | null;
    html_link: string | null;
    status: CrmCalendarEventStatus;
    is_cancelled: boolean;
    external_participants: string[];
    domains: string[];
    attached_to?: { type: string; id: number; name: string | null };
    facility_note_id: number | null;
    resolved_at: string | null;
    suggestions: { type: string; id: number; name: string | null }[];
};

export type CrmCalendarEventFilters = {
    status?: CrmCalendarEventStatus | 'all';
    owner_email?: string;
    search?: string;
};

export function useCrmCalendarEvents(filters: CrmCalendarEventFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, String(value));
    });
    const query = params.toString();

    return useApiQuery<{ data: CrmCalendarEvent[]; meta?: { total: number } }>(
        `/crm/calendar-events${query ? `?${query}` : ''}`
    );
}

/** Compteurs par statut : pastille du menu et onglet « À classer ». */
export function useCrmCalendarEventCounts(enabled = true, refetchInterval: number | false = 60000) {
    return useApiQuery<Record<string, number>>('/crm/calendar-events/counts', {}, { enabled, refetchInterval });
}

/**
 * Associe une réunion à une fiche : elle y devient un RDV, qui couvre la fiche.
 * Le domaine des invités est retenu : les réunions suivantes (agenda ou Noota) se rattachent seules.
 */
export function useAssociateCrmCalendarEvent() {
    const queryClient = useQueryClient();

    return useApiMutation<CrmCalendarEvent, Error, { eventId: number; type: 'facility' | 'prospect'; id: number }>(
        'post',
        (vars) => `/crm/calendar-events/${vars.eventId}/associate`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

/** Écarte une réunion (perso, fournisseur…) : elle reste consultable. */
export function useIgnoreCrmCalendarEvent() {
    const queryClient = useQueryClient();

    return useApiMutation<CrmCalendarEvent, Error, { eventId: number }>(
        'post',
        (vars) => `/crm/calendar-events/${vars.eventId}/ignore`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}
